<template>
  <el-form
    ref="formRef"
    class="flex"
    require-asterisk-position="right"
    label-position="top"
    :model="formValues"
    :rules="rules"
  >
    <div class="up-form-box">
      <el-form-item prop="idFrontImageUrl" label="证件照正面">
        <card-image-upload
          :readonly="disabledFields.includes('idFrontImageUrl')"
          :image="formValues.idFrontImageUrl"
          :action="UploadImageUrls.idNoFront"
          :placeholder-image="ssnFrontImg"
          @on-success="(res: any) => {
            const { idNoFrontInfo, picResult } = res || {};

            formValues.idFrontImageUrl = picResult?.picUrl;
            formValues.userName = idNoFrontInfo?.name;
            formValues.idNumber = idNoFrontInfo?.identNo;
            formValues.location = idNoFrontInfo?.address;

            formRef?.validateField?.('idNumber');
            formRef?.validateField?.('userName');
            formRef?.validateField?.('location');
            formRef?.validateField?.('idFrontImageUrl');
          }"
        />
      </el-form-item>
      <el-form-item prop="idBackImageUrl" label="证件照反面">
        <card-image-upload
          :readonly="disabledFields.includes('idBackImageUrl')"
          :image="formValues.idBackImageUrl"
          :action="UploadImageUrls.idNoBack"
          :placeholder-image="ssnBackImg"
          @on-success="(res: any) => {
            const { idNoBackInfo, picResult } = res || {};

            formValues.idBackImageUrl = picResult?.picUrl;

            if (idNoBackInfo?.startDate && idNoBackInfo?.endDate) {
              formValues.idValidFrom = idNoBackInfo.startDate.replace(/-/g, '');
              formValues.idValidTo = idNoBackInfo.endDate.replace(/-/g, '');
              formValues.idNoExpiryDate = [
                changeFormatToDate(formValues.idValidFrom),
                changeFormatToDate(formValues.idValidTo),
              ];
              formValues.isLongTerm = idNoBackInfo?.endDate === '99991231';
            }

            formRef?.validateField?.('idNoExpiryDate');
            formRef?.validateField?.('idBackImageUrl');
          }"
        />
      </el-form-item>
      <el-form-item prop="userName" label="用户姓名">
        <el-input
          v-model="formValues.userName"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabledFields.includes('userName')"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="idType" label="证件类型">
        <el-select
          v-model="formValues.idType"
          placeholder="请选择"
          clearable
          :disabled="disabledFields.includes('idType')"
        >
          <el-option :label="payeeIdTypeLabel[EPayeeIdType.Ssn]" :value="EPayeeIdType.Ssn" />
        </el-select>
      </el-form-item>
      <el-form-item prop="idNumber" label="证件号">
        <el-input
          v-model="formValues.idNumber"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabledFields.includes('idNumber')"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="idNoExpiryDate" label="证件有效期">
        <el-date-picker
          v-model="formValues.idNoExpiryDate"
          type="daterange"
          clearable
          start-placeholder="创建时间(起)"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          end-placeholder="创建时间(止)"
          @change="isLongTerm = false"
        />
        <el-checkbox
          v-model="isLongTerm"
          style="margin-left: 24px;"
          label="长期"
          :disabled="isLongTerm"
          @change="changeLongTerm()"
        />
      </el-form-item>
      <el-form-item prop="userPhone" label="用户手机号">
        <el-input
          v-model="formValues.userPhone"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="location" label="所在地">
        <el-input
          v-model="formValues.location"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="occupation" label="职业信息">
        <el-select
          v-model="formValues.occupation"
          placeholder="请选择"
          clearable
          filterable
          @keyup.enter="onKeyupEnter"
        >
          <el-option
            v-for="(v, idx) in jobList"
            :key="idx"
            :label="v.label"
            :value="v.value"
          />
        </el-select>
      </el-form-item>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { CardImageUpload } from '@vue3/components';
import { changeFormatToDate, checkPhone, checkSSNId, jobList, scrollToErrorField } from '@core/tools';
import { FormInstance, FormRules } from 'element-plus';
import { computed, PropType, reactive, ref, toRaw, watch } from 'vue';
import { type IPersonalInfo } from './types';
import { EPayeeIdType, payeeIdTypeLabel } from '../../../../types/common';
import { UploadImageUrls } from '../../../../api/common';
import ssnFrontImg from '@app/assets/images/ssn-front.png';
import ssnBackImg from '@app/assets/images/ssn-back.png';
import { getCustomState } from '../../../..';

defineOptions({ name: 'PersonalInfo' });

const props = defineProps({
  data: {
    type: Object as PropType<IPersonalInfo>,
    default: () => {
      return {} as IPersonalInfo;
    },
  },
  /** 自动滚动到错误位置 */
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  /* 是否启用回车键 */
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  /** 禁用字段 */
  disabledFields: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const emit = defineEmits<{'key-enter': []}>();

const personalState = computed(() => getCustomState<{ userName: string; ssnId: string }>());

const formRef = ref<FormInstance>();
const isLongTerm = ref(false);
const formValues = ref({} as IPersonalInfo);

const rules = reactive<FormRules<IPersonalInfo>>({
  userPhone: !props.disabledFields.includes('userPhone')
    ? [
      {
        required: true,
        validator: (rule: any, value: string, callback: any) => {
          if (checkPhone(formValues.value.userPhone)) {
            callback(checkPhone(formValues.value.userPhone, '用户'));
          }
          callback();
        },
      },
    ]
    : undefined,
  userName: !props.disabledFields.includes('userName')
    ? [
      {
        required: true,
        message: '请输入用户姓名',
        trigger: 'blur',
      },
    ]
    : undefined,
  idType: !props.disabledFields.includes('idType')
    ? [
      {
        required: true,
        message: '请选择证件类型',
        trigger: 'change',
      },
    ]
    : undefined,
  idNumber: !props.disabledFields.includes('idNumber')
    ? [
      {
        required: true,
        validator: (rule: any, value: string, callback: any) => {
          if (checkSSNId(formValues.value.idNumber)) {
            callback(checkSSNId(formValues.value.idNumber));
          }
          callback();
        },
      },
    ]
    : undefined,
  idFrontImageUrl: !props.disabledFields.includes('idFrontImageUrl')
    ? [
      {
        required: true,
        message: '请选择身份证人像面',
      },
    ]
    : undefined,
  idBackImageUrl: !props.disabledFields.includes('idBackImageUrl')
    ? [
      {
        required: true,
        message: '请选择身份证国徽面',
      },
    ]
    : undefined,
  idNoExpiryDate: !props.disabledFields.includes('idNoExpiryDate')
    ? [
      {
        required: true,
        message: '请选择身份证有效期',
        trigger: 'blur',
      },
    ]
    : undefined,
  location: !props.disabledFields.includes('location')
    ? [
      {
        required: true,
        message: '请输入所在地',
      },
    ]
    : undefined,
  occupation: !props.disabledFields.includes('occupation')
    ? [
      {
        required: true,
        message: '请选择职业信息',
      },
    ]
    : undefined,
});

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const valid = await validate();

  if (!valid) {
    if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    return;
  }

  emit('key-enter');
}

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    } else {
      if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    }
  });

  return valid;
}

function changeLongTerm() {
  if (isLongTerm.value) {
    const time = formValues.value.idNoExpiryDate;

    formValues.value.idNoExpiryDate = [!time || !time?.[0] ? '1950-01-01' : time[0], '2999-12-31'];
    formRef.value?.validateField('idNoExpiryDate');
  }
}

function resetFormValues() {
  const d = JSON.parse(JSON.stringify(props.data)) as IPersonalInfo;

  formRef.value?.resetFields?.();
  formValues.value = {
    idType: d.idType || EPayeeIdType.Ssn,
    userName: d.userName || personalState.value?.userName,
    idNumber: d.idNumber || personalState.value?.ssnId,
    userPhone: d.userPhone,
    idFrontImageUrl: d.idFrontImageUrl,
    idBackImageUrl: d.idBackImageUrl,
    idValidFrom: d.idValidFrom,
    idValidTo: d.idValidTo,
    location: d.location,
    occupation: d.occupation,
  };

  mergeDate(formValues.value);
}

function mergeDate(d: IPersonalInfo) {
  if (d.idValidFrom && d.idValidTo) {
    d.idNoExpiryDate = [
      changeFormatToDate(d.idValidFrom),
      changeFormatToDate(d.idValidTo),
    ];
  }
  if (d.idValidTo === '2999-12-31') {
    isLongTerm.value = true;
  }
}

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => ({
    ...toRaw(formValues.value),
    idValidFrom: formValues.value.idNoExpiryDate?.[0]?.replace(/-/g, ''),
    idValidTo: formValues.value.idNoExpiryDate?.[1]?.replace(/-/g, ''),
    isLongTerm: isLongTerm.value,
    idNoExpiryDate: undefined,
  }),
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});

watch(
  () => props.data,
  () => {
    resetFormValues();
  },
  { immediate: true },
);
</script>

<style scoped lang="scss" src="../index.scss" />

